import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { FileSpreadsheet, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import { Modal } from '../ui-v2.5/Modal';
import { estadoEfectivo } from '../../elementos/eventos/filtros/estadoEfectivo';
import { UsuarioContext } from '../../providers/personaContext';
import {
  fetchCamposExportables, fetchExportarXLSX, mensajeDeError, descargarBlob, nombreFichero,
} from '../../../peticiones_API/PeticionesExportacion';

/**
 * Fase B · §4.5 — el botón de exportar y su selector de campos.
 *
 * ── CINCO GRUPOS ──────────────────────────────────────────────────────────────────────────
 *
 * Contacto (10) · administrativo (3) · laboral (2) · vacaciones (8) · personales (6).
 *
 * Negocio, departamento, cargo y oficina se quedan en contacto: están en el organigrama, los ve
 * todo el mundo, y son lo que hace útil un directorio. Lo demás es gestión de personas.
 *
 * Los títulos y el reparto **no se deciden aquí**: cada campo llega del servidor con su `grupo`.
 * Esta lista sólo les pone nombre y orden, y **un grupo sin campos no se pinta**. Por eso añadir un
 * grupo o un rol al servidor no obliga a tocar este componente — sólo a darle un título.
 *
 * ── LOS PERSONALES NO ESTÁN DESHABILITADOS: NO ESTÁN ──────────────────────────────────────
 *
 * Y no porque este componente los esconda — **no llegan**. El servidor resuelve la lista desde el
 * token, así que quien no es Super Administrador recibe doce campos y no sabe que existen seis
 * más. Esconder una opción es decir que existe; no mandarla, no.
 *
 * Es la misma decisión que el mensaje de rechazo del §4.4, que dice qué campo no está permitido
 * sin revelar cuáles lo estarían para otro rol.
 *
 * ── LOS FILTROS SALEN DE LA URL ───────────────────────────────────────────────────────────
 *
 * De la URL, que es de donde los lee la propia tabla. No de un estado paralelo: **lo que se
 * descarga es literalmente lo que se está viendo**, y no hay forma de que se desincronicen.
 *
 * Con una traducción obligatoria: la URL dice `verificacion` y el servidor espera
 * `segundo_factor`. Son dos nombres para lo mismo y **no falla cuando no casan** — el parámetro no
 * llega, el filtro no se aplica, y el fichero sale con las 270 filas en vez de las 147. La deuda
 * está anotada; mientras exista, la traducción vive aquí.
 *
 * ── EL NÚMERO ES EL DEL FILTRO, NO EL DE LA PÁGINA ────────────────────────────────────────
 *
 * `meta.total` es lo que el servidor dice que selecciona el filtro. La página visible son 25.
 */

/** Traduce la URL al cuerpo que espera el servidor. Una sola vez, y aquí. */
function filtrosDesdeURL(search, esColaboradores, estados, rol) {
  const p = new URLSearchParams(search);
  const activo = p.get('activo') === 'true' ? true : (p.get('activo') === 'false' ? false : null);
  // Sólo 'con' o 'sin' viajan; cualquier otro valor se descarta en vez de llegar al servidor.
  const verificacion = ['con', 'sin'].includes(p.get('verificacion')) ? p.get('verificacion') : null;
  // Éste NO se traduce: la URL dice `empresacontratante` y el servidor espera
  // `empresa_contratante`, que es el mismo nombre sin el guion bajo que la URL no lleva.
  //
  // Dos formas, las MISMAS que valida el listado: 'sin' o el id de una empresa. Si aquí se
  // quedara la lista vieja —'sin' y 'con'— filtrar por una razón social concreta no viajaría al
  // Excel: la pantalla enseñaría 12 filas y el fichero traería las 279, sin dar ningún error.
  const ecURL = p.get('empresacontratante');
  const empresaContratante = (ecURL === 'sin' || /^[0-9]+$/.test(ecURL || '')) ? ecURL : null;

  const comunes = {
    filteredWord: p.get('busqueda'),
    rol: p.get('rol'),
    /* EL ESTADO NO SE LEE EN CRUDO, y aqui estuvo el tercer fallo de esta forma.
     *
     * El filtro NO nace vacio: nace en «Alta», y esa regla vivia SOLO dentro del listado. Leer
     * `estado` a secas daba `null` cuando el usuario no habia tocado nada —el Excel traia 274
     * filas donde la pantalla enseñaba 223— y daba la cadena `'todos'` al elegir Todos, que el
     * servidor metia tal cual en el WHERE: el fichero bajaba VACIO.
     *
     * De los dos, el vacio es peor: un fichero sin filas se lee como «no hay datos».
     *
     * Ahora se resuelve con `estadoEfectivo`, LA MISMA funcion que usa el listado para decidir que
     * pide al servidor. No se escribe nada en la URL: eso se intento y dio un bucle, y ademas no
     * hacia falta —el defecto se resuelve igual en cualquier pestaña, porque no es una eleccion
     * del usuario sino el mismo para todos—.
     *
     * Es la unica forma de que los dos caminos no puedan discrepar: no comparten el dato, comparten
     * la REGLA, y la regla vive en un modulo sin React que ademas se puede probar desde Node.
     * (Hasta el hotfix del 23/10 el listado llevaba una copia en linea; ahora llama a esta.) */
    estado: estadoEfectivo(p.get('estado'), estados, rol),
    negocio: p.get('negocio'),       // en colaboradoras es la empresa colaboradora
    activo,
  };
  if (esColaboradores) return comunes;

  return {
    ...comunes,
    departamento: p.get('departamento'),
    mostrar_dependientes: p.get('mostrar_dependientes') === 'true',
    segundo_factor: verificacion,    // ← la traducción
    // Si RRHH filtra «sin asignar» y ve 270 en pantalla, el fichero tiene que traer esas 270 y
    // no las de siempre: es el Excel con el que va a repartir el trabajo. No viaja en
    // colaboradoras porque esa tabla no tiene la columna.
    empresa_contratante: empresaContratante,
  };
}

const GRUPOS = [
  { clave: 'contacto', titulo: 'Contacto', nota: 'La agenda de la empresa. Se ve en el listado.' },
  { clave: 'administrativo', titulo: 'Administrativo', nota: 'Rol, situación y verificación en dos pasos.' },
  { clave: 'laboral', titulo: 'Laboral', nota: 'Alta, baja y empresa contratante.' },
  { clave: 'vacaciones', titulo: 'Vacaciones', nota: 'Calendario, jornada y días.' },
  { clave: 'personales', titulo: 'Datos personales', nota: 'Sólo Super Administrador.' },
];

export function BotonExportarExcel({ esColaboradores = false, total = 0, estados = [] }) {
  const { search } = useLocation();
  const sesion = useContext(UsuarioContext);
  const rol = sesion?.persona?.rol;
  const listado = esColaboradores ? 'colaboradoras' : 'personas';

  const [catalogo, setCatalogo] = useState(null);   // null = aún no se sabe
  const [abierto, setAbierto] = useState(false);
  const [marcados, setMarcados] = useState([]);
  const [exportando, setExportando] = useState(false);

  // El catálogo se pide UNA VEZ por listado. Si falla, el botón sigue sirviendo para descargar los
  // básicos: la lista es para ELEGIR, y no poder elegir no es no poder exportar.
  useEffect(() => {
    let vivo = true;
    fetchCamposExportables(listado)
      .then((d) => { if (vivo) setCatalogo(d); })
      .catch(() => { if (vivo) setCatalogo({ elige: false, campos: [] }); });
    return () => { vivo = false; };
  }, [listado]);

  // Se agrupa por lo que dice el servidor, no por una lista propia.
  const porGrupo = useMemo(() => {
    const m = {};
    for (const c of (catalogo?.campos || [])) (m[c.grupo] = m[c.grupo] || []).push(c);
    return m;
  }, [catalogo]);

  // Marcados por defecto: **sólo los de contacto**. Es lo que se lleva quien no elige, así que es
  // también el punto de partida honesto para quien sí: lo administrativo y lo personal se piden a
  // propósito, no se arrastran sin querer al pulsar Descargar.
  const clavesContacto = useMemo(() => (porGrupo.contacto || []).map((c) => c.clave), [porGrupo]);
  useEffect(() => { setMarcados(clavesContacto); }, [clavesContacto]);

  const descargar = useCallback(async (campos) => {
    setExportando(true);
    try {
      const blob = await fetchExportarXLSX(listado, filtrosDesdeURL(search, esColaboradores, estados, rol), campos);
      descargarBlob(blob, nombreFichero(listado));
      setAbierto(false);
      toast.success('Excel descargado. La descarga ha quedado registrada.');
    } catch (error) {
      toast.error(await mensajeDeError(error));
    } finally {
      setExportando(false);
    }
  }, [listado, search, esColaboradores, estados, rol]);

  const alPulsar = () => {
    // Quien no elige, descarga. Sin pasos intermedios: un diálogo con una sola opción posible es
    // un clic de más.
    if (catalogo?.elige) setAbierto(true);
    else descargar(null);
  };

  const alternar = (clave) => setMarcados((m) =>
    (m.includes(clave) ? m.filter((x) => x !== clave) : [...m, clave]));

  const conteo = Number(total) || 0;
  const etiquetaFilas = `${conteo} ${conteo === 1 ? 'fila' : 'filas'}`;

  return (
    <>
      <Button
        onClick={alPulsar}
        disabled={exportando || catalogo === null}
        variant="outlined"
        startIcon={exportando
          ? <CircularProgress size={16} sx={{ color: 'rgba(0, 60, 102, 0.8)' }} />
          : <FileSpreadsheet size={18} />}
        sx={{
          margin: '10px',
          textTransform: 'none',
          color: 'rgba(0, 60, 102, 0.8)',
          borderColor: 'rgba(0, 60, 102, 0.4)',
          whiteSpace: 'nowrap',
          '&:hover': {
            color: 'rgba(0, 177, 195, 0.9)',
            borderColor: 'rgba(0, 177, 195, 0.9)',
            backgroundColor: 'white',
          },
        }}
      >
        {exportando ? 'Generando…' : 'Exportar a Excel'}
      </Button>

      <Modal
        open={abierto}
        onClose={() => !exportando && setAbierto(false)}
        size="lg"
        title="Exportar a Excel"
        subtitle={`Se exportarán ${etiquetaFilas}, las que selecciona el filtro actual — no sólo las de esta página.`}
        footer={(
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setAbierto(false)}
              disabled={exportando}
              className="rounded-v25-md border border-v25-line bg-white px-4 py-2 text-[13px] font-semibold text-v25-ink-700 shadow-v25-btn disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => descargar(marcados)}
              disabled={exportando || marcados.length === 0}
              className="rounded-v25-md bg-v25-brand-800 px-4 py-2 text-[13px] font-semibold text-white shadow-v25-btn disabled:opacity-50"
            >
              {exportando ? 'Generando…' : `Descargar ${etiquetaFilas}`}
            </button>
          </div>
        )}
      >
        <div className="space-y-4">
          {GRUPOS.map(({ clave, titulo, nota }) => {
            const campos = porGrupo[clave] || [];
            // AUSENTES, no deshabilitados: los grupos que ese rol no puede exportar llegan VACÍOS
            // desde el servidor y el bloque entero no se pinta. No es que se escondan aquí.
            if (campos.length === 0) return null;
            return (
              <section key={clave}>
                <header className="mb-2 flex items-baseline gap-2">
                  <h3 className="text-[13px] font-semibold text-v25-ink-900">{titulo}</h3>
                  <span className="text-[12px] text-v25-ink-500">{nota}</span>
                </header>
                <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2 md:grid-cols-3">
                  {campos.map((c) => (
                    <label key={c.clave} className="flex cursor-pointer items-center gap-2 rounded-v25-sm px-1 py-1 text-[13px] text-v25-ink-700 hover:bg-v25-surface-sunken">
                      <input
                        type="checkbox"
                        checked={marcados.includes(c.clave)}
                        onChange={() => alternar(c.clave)}
                        className="h-4 w-4 shrink-0 accent-[#003c66]"
                      />
                      <span>{c.etiqueta}</span>
                    </label>
                  ))}
                </div>
              </section>
            );
          })}

          {/*
            Se dice por qué NO están, en vez de dejar el hueco sin explicar. Que no aparezcan es la
            decisión —un fichero no caduca, no se audita después de descargarse, y acaba en un
            correo—, y una ausencia sin motivo se lee como un olvido.
          */}
          <div className="flex items-start gap-2 rounded-v25-md border border-v25-info-bd bg-v25-info-bg px-3 py-2">
            <AlertTriangle size={15} className="mt-[2px] shrink-0 text-v25-info-fg" />
            <p className="text-[12px] leading-relaxed text-v25-info-fg">
              Las contraseñas y los datos de soporte <strong>no se exportan</strong>, para ningún rol.
              Un fichero no caduca ni deja rastro después de descargarse; para consultarlos está la
              ventana de diez minutos. Esta descarga queda registrada con quién, qué campos y cuántas filas.
            </p>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default BotonExportarExcel;
